/* ============================================================
   Fence Works — a supplier's price list onto the rate cards
   (docs/SERVICE_SWITCHER_PLAN.md §13 item 18). Pure, import-free.

   The foam distributor and the coating supplier each send a sheet: SKU,
   item, price per unit and/or per pack. This matches every row to the
   recipe components under the concrete rate cards (and the coat finishes'
   own component lists) — by SKU first, then by name — and says what each
   price would become. Nothing is written here; the Rate cards page shows
   the preview and applies the ones the owner keeps. The engine prices in
   $/unit (component.cost) and orders in packs (component.pack.cost), so a
   sheet that gives one is converted to the other through the pack size.
   ============================================================ */
export type PriceItem = { sku: string; desc: string; unitCost: number | null; packSize: number | null; packUnit: string; packCost: number | null; supplier: string; row: number };
export type PriceChange = {
  jobType: string; systemId: string | null; index: number; desc: string; sku: string; supplier: string; matchBy: "sku" | "name";
  from: { cost: number | null; packCost: number | null }; to: { cost: number | null; packCost: number | null }; pct: number | null;
};

/* the column names a sheet may use, normalised (lower-case, letters and digits only) */
export const PRICE_COLS: Record<string, string[]> = {
  sku: ["sku", "itemno", "itemnumber", "item", "partno", "partnumber", "productcode", "code", "mfgno"],
  desc: ["description", "desc", "itemdescription", "productname", "product", "name", "itemname", "title"],
  unitCost: ["unitcost", "unitprice", "priceperunit", "costperunit", "each", "eachprice", "price", "cost", "yourprice", "netprice", "dealerprice"],
  packSize: ["packsize", "packqty", "qtyperpack", "unitsperpack", "casesize", "caseqty", "setsize", "kitsize", "size", "qty"],
  packUnit: ["packunit", "packuom", "uom", "unit", "units", "per"],
  packCost: ["packcost", "packprice", "caseprice", "casecost", "setprice", "setcost", "kitprice", "kitcost", "priceperpack", "priceperset", "priceperkit"],
  supplier: ["supplier", "vendor", "distributor", "brand", "manufacturer", "mfg"],
};
const normHeader = (h: any) => String(h || "").toLowerCase().replace(/[^a-z0-9]/g, "");
export const normDesc = (s: any) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const money = (v: any): number | null => {
  if (v == null) return null;
  const s = String(v).replace(/[$,\s]/g, "").replace(/^\((.*)\)$/, "-$1");
  if (!s || !/^-?\d*\.?\d+$/.test(s)) return null;
  const n = Number(s);
  return isFinite(n) && n >= 0 ? Math.round(n * 10000) / 10000 : null;
};
const r2 = (n: number) => Math.round(n * 100) / 100;
const r4 = (n: number) => Math.round(n * 10000) / 10000;

/* which header holds which field; a header is used once, first match wins;
   "item" means the SKU only when a separate description column exists */
export function mapPriceHeaders(headers: any): Record<string, string> {
  const hs: string[] = (Array.isArray(headers) ? headers : []).map((h) => String(h || ""));
  const used = new Set<string>();
  const out: Record<string, string> = {};
  const order = ["sku", "desc", "packCost", "unitCost", "packSize", "packUnit", "supplier"];
  for (const field of order) {
    for (const syn of PRICE_COLS[field]) {
      const hit = hs.find((h) => !used.has(h) && normHeader(h) === syn);
      if (hit) { out[field] = hit; used.add(hit); break; }
    }
  }
  // "item" alone (no description column) names the product, not a code
  if (out.sku && !out.desc && /^(item|product|name)$/.test(normHeader(out.sku))) { out.desc = out.sku; delete out.sku; }
  return out;
}

/* the rows worth matching: something to match on and at least one price */
export function parsePriceRows(records: any, map: Record<string, string>): PriceItem[] {
  const rows: PriceItem[] = [];
  (Array.isArray(records) ? records : []).forEach((r: any, i: number) => {
    if (!r || typeof r !== "object") return;
    const get = (f: string) => (map[f] ? String(r[map[f]] == null ? "" : r[map[f]]).trim() : "");
    const sku = get("sku").slice(0, 60), desc = get("desc").slice(0, 160);
    const unitCost = money(get("unitCost")), packCost = money(get("packCost"));
    const ps = money(get("packSize"));
    const item: PriceItem = { sku, desc, unitCost, packSize: ps && ps > 0 ? ps : null, packUnit: get("packUnit").slice(0, 24), packCost, supplier: get("supplier").slice(0, 80), row: i + 2 };
    if (!(sku || desc)) return;
    if (item.unitCost == null && item.packCost == null) return;
    rows.push(item);
  });
  return rows;
}

/* every component a card prices: the card's own list and, on the coat card, each finish's */
function componentSlots(cards: any): { jobType: string; systemId: string | null; index: number; comp: any }[] {
  const out: { jobType: string; systemId: string | null; index: number; comp: any }[] = [];
  Object.keys(cards || {}).forEach((jt) => {
    const c = cards[jt];
    if (!c) return;
    (Array.isArray(c.components) ? c.components : []).forEach((comp: any, index: number) => { if (comp && comp.kind !== "labor") out.push({ jobType: jt, systemId: null, index, comp }); });
    (Array.isArray(c.systems) ? c.systems : []).forEach((sys: any) => (Array.isArray(sys && sys.components) ? sys.components : []).forEach((comp: any, index: number) => { if (comp && comp.kind !== "labor") out.push({ jobType: jt, systemId: String(sys.id || sys.name || ""), index, comp }); }));
  });
  return out;
}
function nameMatch(a: string, b: string): boolean {
  const x = normDesc(a), y = normDesc(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const short = x.length < y.length ? x : y, long = x.length < y.length ? y : x;
  return short.length >= 8 && long.indexOf(short) > -1;
}

/* what the sheet would change: one entry per component it reaches, with the
   old and new unit and pack prices. Unmatched rows come back for the owner. */
export function matchPriceList(cards: any, items: PriceItem[]): { changes: PriceChange[]; unmatched: PriceItem[]; unchanged: number } {
  const slots = componentSlots(cards);
  const changes: PriceChange[] = [];
  const unmatched: PriceItem[] = [];
  let unchanged = 0;
  const taken = new Set<string>();
  (items || []).forEach((it) => {
    const bySku = it.sku ? slots.filter((s) => s.comp.sku && String(s.comp.sku).trim().toLowerCase() === it.sku.toLowerCase()) : [];
    const hits = bySku.length ? bySku : (it.desc ? slots.filter((s) => nameMatch(s.comp.desc, it.desc)) : []);
    if (!hits.length) { unmatched.push(it); return; }
    hits.forEach((s) => {
      const id = s.jobType + "/" + (s.systemId || "") + "/" + s.index;
      if (taken.has(id)) return;
      const comp = s.comp;
      const packSize = Number(it.packSize) > 0 ? Number(it.packSize) : (comp.pack && Number(comp.pack.size) > 0 ? Number(comp.pack.size) : null);
      const fromCost = comp.cost == null || comp.cost === "" ? null : Number(comp.cost);
      const fromPack = comp.pack && comp.pack.cost != null && comp.pack.cost !== "" ? Number(comp.pack.cost) : null;
      // the sheet's unit price wins for the $/unit; a pack-only sheet divides by the pack
      let toCost: number | null = it.unitCost != null ? it.unitCost : (it.packCost != null && packSize ? r4(it.packCost / packSize) : null);
      let toPack: number | null = it.packCost != null ? it.packCost : (toCost != null && packSize ? r2(toCost * packSize) : fromPack);
      if (toCost == null && toPack == null) { unmatched.push(it); return; }
      if (toCost == null) toCost = fromCost;
      const same = (a: number | null, b: number | null) => (a == null && b == null) || (a != null && b != null && Math.abs(a - b) < 0.005);
      if (same(fromCost, toCost) && same(fromPack, toPack)) { unchanged++; taken.add(id); return; }
      taken.add(id);
      changes.push({
        jobType: s.jobType, systemId: s.systemId, index: s.index, desc: String(comp.desc || ""), sku: it.sku || String(comp.sku || ""), supplier: it.supplier || String(comp.supplier || ""), matchBy: bySku.length ? "sku" : "name",
        from: { cost: fromCost, packCost: fromPack }, to: { cost: toCost, packCost: toPack },
        pct: fromCost && toCost != null ? Math.round(((toCost - fromCost) / fromCost) * 1000) / 10 : null,
      });
    });
  });
  return { changes, unmatched, unchanged };
}

/* the cards with the kept changes written in: $/unit, the pack price (and a
   pack created when the sheet gave a size), the SKU and supplier filled in */
export function applyPriceList(cards: any, changes: PriceChange[], now = Date.now()): any {
  const out: any = { ...(cards || {}) };   // an untouched card stays the same object
  (changes || []).forEach((ch) => {
    const card = out[ch.jobType];
    if (!card) return;
    const patch = (comp: any) => {
      const next: any = { ...comp };
      if (ch.to.cost != null) next.cost = ch.to.cost;
      if (ch.to.packCost != null) {
        const size = comp.pack && Number(comp.pack.size) > 0 ? Number(comp.pack.size) : (ch.to.cost ? r2(ch.to.packCost / ch.to.cost) : null);
        if (size) next.pack = { size, unit: (comp.pack && comp.pack.unit) || "pack", ...(comp.pack || {}), cost: ch.to.packCost };
      }
      if (ch.sku && !comp.sku) next.sku = ch.sku;
      if (ch.supplier && String(ch.supplier) !== String(comp.supplier || "")) next.supplier = ch.supplier;
      return next;
    };
    if (ch.systemId == null) {
      const list = Array.isArray(card.components) ? card.components.slice() : [];
      if (list[ch.index]) list[ch.index] = patch(list[ch.index]);
      out[ch.jobType] = { ...card, components: list, updatedAt: now };
    } else {
      const systems = (Array.isArray(card.systems) ? card.systems : []).map((sys: any) => {
        if (String(sys.id || sys.name || "") !== ch.systemId) return sys;
        const list = Array.isArray(sys.components) ? sys.components.slice() : [];
        if (list[ch.index]) list[ch.index] = patch(list[ch.index]);
        return { ...sys, components: list };
      });
      out[ch.jobType] = { ...card, systems, updatedAt: now };
    }
  });
  return out;
}

export function priceListSummary(changes: PriceChange[]): { count: number; up: number; down: number; avgPct: number | null } {
  const pcts = (changes || []).map((c) => c.pct).filter((p): p is number => p != null);
  return { count: (changes || []).length, up: pcts.filter((p) => p > 0).length, down: pcts.filter((p) => p < 0).length, avgPct: pcts.length ? Math.round((pcts.reduce((a, b) => a + b, 0) / pcts.length) * 10) / 10 : null };
}
